import { useEffect, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@/components/ui/Alert';
import {
  createWallet,
  getBankAccount,
  getWalletData,
  getWithdrawals,
  requestWithdrawal,
  WalletBankAccountResponse,
  WalletDataResponse,
  WithdrawalResponse,
} from '@/services/walletService';
import { invoiceService, TransactionApiResponse } from '@/services/invoiceService';
import { Icon } from '@iconify/react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { getArenaSlug } from '@/utils/arena';
import Notification from '@/components/ui/Notification';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { getAdminBasePath } from '@/utils/adminPaths';

interface WithdrawFormValues {
  amount: number;
}

const withdrawValidationSchema = Yup.object({
  amount: Yup.number()
    .min(13000, 'Minimal withdraw Rp 13.000')
    .required('Jumlah penarikan wajib diisi'),
});

const Wallet: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'income' | 'withdrawals'>('income');
  const [withdrawModalOpen, setWithdrawModalOpen] = useState<boolean>(false);
  const [walletData, setWalletData] = useState<WalletDataResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalResponse | null>(null);
  const [bankAccountData, setBankAccountData] = useState<WalletBankAccountResponse | null>(null);
  const [transactionData, setTransactionData] = useState<TransactionApiResponse | null>(null);
  const [transactionPage, setTransactionPage] = useState<number>(1);
  const [transactionLimit] = useState<number>(10);
  const [notificationOpen, setNotificationOpen] = useState<boolean>(false);
  const [withdrawAmount, setWithdrawAmount] = useState<number>(0);
  const [withdrawalCode, setWithdrawalCode] = useState<string>('');
  const [withdrawalIsError, setWithdrawalIsError] = useState<boolean>(false);
  const [withdrawConfirm, setWithdrawConfirm] = useState<boolean>(false);
  const [withdrawConfirmMessage, setWithdrawConfirmMessage] = useState<string>('');
  const [isWithdrawConfirmed, setIsWithdrawConfirmed] = useState<boolean>(false);
  const [isSubmitWithdraw, setIsSubmitWithdraw] = useState<boolean>(false);
  const [transactionSortBy, setTransactionSortBy] = useState<string>('date');
  const [transactionSortOrder, setTransactionSortOrder] = useState<'asc' | 'desc'>('desc');
  const [withdrawalSortBy, setWithdrawalSortBy] = useState<string>('date');
  const [withdrawalSortOrder, setWithdrawalSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentWithdrawValues, setCurrentWithdrawValues] = useState<WithdrawFormValues | null>(
    null
  );

  // Handle withdraw confirmation
  useEffect(() => {
    if (isWithdrawConfirmed && currentWithdrawValues) {
      handleWithdraw(currentWithdrawValues).finally(() => {
        setWithdrawModalOpen(false);
        setIsWithdrawConfirmed(false);
        setCurrentWithdrawValues(null);
        setWithdrawConfirm(false);
        setWithdrawConfirmMessage('');
      });
    }
  }, [isWithdrawConfirmed, currentWithdrawValues]);

  // fetch wallet data
  const fetchWalletData = async (page: number = 1) => {
    setLoading(true);
    setError(null);
    setIsError(false);

    const errors: string[] = [];

    try {
      const result = await getWalletData();
      setWalletData(result);

      if (result.error) {
        errors.push(`Wallet Data: ${result.error}`);
      }

      const withdrawalResult = await getWithdrawals(withdrawalSortBy, withdrawalSortOrder);
      if (withdrawalResult.error) {
        errors.push(`Withdrawals: ${withdrawalResult.error}`);
      } else {
        setWithdrawalData(withdrawalResult);
      }

      // Fetch Bank Account for "Rekening Pencairan" Card
      try {
        const bankRes = await getBankAccount();
        if (bankRes && !bankRes.error) {
          setBankAccountData(bankRes);
        }
      } catch (_bankErr) {
        // ignore
      }

      // Fetch online transactions
      const arenaSlug = getArenaSlug();
      if (arenaSlug) {
        try {
          const transactionResult = await invoiceService.getArenaOnlineTransactions(
            page,
            transactionLimit,
            transactionSortBy,
            transactionSortOrder
          );
          setTransactionData(transactionResult);
        } catch (transactionError: any) {
          errors.push(`Transactions: ${transactionError.message}`);
        }
      } else {
        errors.push('Arena slug not found');
      }

      if (errors.length > 0) {
        setIsError(true);
        setError(errors.join(' | '));
      }
    } catch (error) {
      console.error('Unexpected error in fetchWalletData:', error);
      setIsError(true);
      setError('Terjadi kesalahan saat memuat data dompet');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData(transactionPage);
  }, [
    transactionPage,
    transactionSortBy,
    transactionSortOrder,
    withdrawalSortBy,
    withdrawalSortOrder,
  ]);

  const handleTransactionPageChange = (page: number) => {
    setTransactionPage(page);
  };

  const handleTransactionSort = (key: string) => {
    if (transactionSortBy === key) {
      setTransactionSortOrder(transactionSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setTransactionSortBy(key);
      setTransactionSortOrder('desc');
    }
    setTransactionPage(1);
  };

  const handleWithdrawalSort = (key: string) => {
    if (withdrawalSortBy === key) {
      setWithdrawalSortOrder(withdrawalSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setWithdrawalSortBy(key);
      setWithdrawalSortOrder('desc');
    }
  };

  const handleWithdrawPopup = async () => {
    const result = await getBankAccount();
    setBankAccountData(result);
    setWithdrawModalOpen(true);
  };

  const handleWithdraw = async (values: WithdrawFormValues) => {
    setIsSubmitWithdraw(true);

    const adminFee = walletData?.withdraw_fee ?? 0;
    const netAmount = Math.max(0, values.amount - adminFee);
    const result = await requestWithdrawal(netAmount);
    if (result.error) {
      setError(result.error);
      setIsError(true);
    } else {
      setWithdrawModalOpen(false);
      fetchWalletData(transactionPage);

      const withdrawalResult = await getWithdrawals();
      setWithdrawalData(withdrawalResult);

      setWithdrawAmount(netAmount);
      if (result?.error) {
        setWithdrawalIsError(true);
      }
      setWithdrawalCode(result.code || '');
      setNotificationOpen(true);
    }

    setIsSubmitWithdraw(false);
  };

  const renderWithdrawalStatusChip = (status: string) => {
    const s = status.toLowerCase();
    if (s === 'completed' || s === 'paid' || s === 'selesai' || s === 'berhasil') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          Berhasil
        </span>
      );
    }
    if (s === 'failed' || s === 'canceled' || s === 'gagal' || s === 'dibatalkan') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-red-50 text-red-600 border border-red-200">
          Gagal
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
        Diproses
      </span>
    );
  };

  const renderTransactionStatusChip = (status: string) => {
    const s = status.toUpperCase();
    if (s === 'SUCCESSFUL' || s === 'COMPLETED' || s === 'PAID') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
          Selesai
        </span>
      );
    }
    if (s === 'PENDING') {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-[11px] font-semibold bg-gray-50 text-gray-600 border border-gray-200">
        {status}
      </span>
    );
  };

  const maskAccountNumber = (acc?: string) => {
    if (!acc) return '-';
    if (acc.length <= 4) return acc;
    const first = acc.slice(0, 4);
    const last = acc.slice(-4);
    return `${first} •••• ${last}`;
  };

  const formatDateOnly = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    const datePart = d.toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
    const timePart = d
      .toLocaleTimeString('id-ID', {
        hour: '2-digit',
        minute: '2-digit',
      })
      .replace('.', ':');
    return `${datePart} · ${timePart}`;
  };

  const getPaginationRange = (currentPage: number, totalPages: number) => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    if (currentPage <= 3) {
      return [1, 2, 3, 4, '...', totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  useEffect(() => {
    const init = async () => {
      try {
        await createWallet();
      } catch (err) {
        console.error('Failed to create wallet:', err);
      }
    };
    init();
  }, []);

  const totalTransactionCount = transactionData?.pagination?.total ?? 0;
  const totalWithdrawalCount = withdrawalData?.withdrawals?.length ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalTransactionCount / transactionLimit));
  const showingStart = Math.min((transactionPage - 1) * transactionLimit + 1, totalTransactionCount);
  const showingEnd = Math.min(transactionPage * transactionLimit, totalTransactionCount);
  const paginationPages = getPaginationRange(transactionPage, totalPages);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-8xl mx-auto">
      {/* Page Header */}
      <div>
        <h1 className="text-xl md:text-2xl font-semibold text-[#0E1726]">Dompet</h1>
      </div>

      {/* Error Alert */}
      {isError && error && (
        <Alert type="danger" title="Gagal Memuat Data Dompet" body={error} isDismissable />
      )}

      {/* Top 2 Cards: Wallet Balance (Dark) + Rekening Pencairan (White) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-stretch">
        {/* Left Card: Dark Card (Screenshot 1) */}
        <div className="lg:col-span-7 bg-[#0E1726] text-white rounded-2xl p-6 md:p-7 shadow-md flex flex-col justify-between relative overflow-hidden">
          <div>
            {/* Header: Label + Sync */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-400 font-medium">Total saldo di wallet</span>
              <button
                type="button"
                onClick={() => fetchWalletData(transactionPage)}
                disabled={loading}
                className="h-7 px-3 rounded-full bg-white/10 hover:bg-white/15 text-white text-xs font-semibold flex items-center gap-1.5 transition-colors cursor-pointer border border-white/10"
              >
                <Icon
                  icon="ph:arrows-clockwise-bold"
                  className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`}
                />
                <span>Sync</span>
              </button>
            </div>

            {/* Main Balance */}
            <div className={`text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight my-4 ${loading ? 'animate-pulse' : ''}`}>
              Rp. {walletData ? walletData.balance.toLocaleString('id-ID') : '0'}
            </div>

            {/* Sub-balances Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-white/10">
              {/* Saldo Pending */}
              <div>
                <div className="flex items-center gap-1.5 text-xs text-gray-400 font-medium">
                  <Icon icon="ph:hourglass-bold" className="w-3.5 h-3.5 text-gray-400" />
                  <span>Saldo pending</span>
                </div>
                <div className="text-sm sm:text-base font-semibold text-white mt-1">
                  Rp. {walletData ? walletData.saldo_pending.toLocaleString('id-ID') : '0'}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  Waktu tunggu 1 hari kerja setelah transaksi
                </div>
              </div>

              {/* Siap Ditarik */}
              <div>
                <div className="flex items-center gap-1.5 text-xs text-emerald-400 font-medium">
                  <Icon icon="ph:check-circle-bold" className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Siap ditarik</span>
                </div>
                <div className="text-sm sm:text-base font-semibold text-emerald-400 mt-1">
                  Rp. {walletData ? walletData.balance.toLocaleString('id-ID') : '0'}
                </div>
                <div className="text-[10px] text-gray-400 mt-0.5">
                  Biaya penarikan Rp. {(walletData?.withdraw_fee ?? 2000).toLocaleString('id-ID')}
                </div>
              </div>
            </div>
          </div>

          {/* Tarik Saldo Button */}
          <button
            type="button"
            onClick={handleWithdrawPopup}
            className="w-full h-11 bg-[#FC480D] hover:bg-[#E8490F] text-white text-sm font-semibold rounded-full flex items-center justify-center gap-2 transition-all cursor-pointer shadow-md active:scale-[0.99] mt-6"
          >
            <Icon icon="ph:arrow-down-bold" className="w-4 h-4" />
            <span>Tarik Saldo</span>
          </button>
        </div>

        {/* Right Card: Rekening Pencairan (Screenshot 1) */}
        <div className="lg:col-span-5 bg-white rounded-2xl border border-gray-200/80 p-6 shadow-2xs flex flex-col justify-between">
          <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#0E1726]">Rekening Pencairan</h2>
              <button
                type="button"
                onClick={() => navigate(`${getAdminBasePath()}/profile-settings`)}
                className="h-7 px-3 rounded-full bg-orange-50 hover:bg-orange-100/70 text-[#FC480D] text-xs font-semibold flex items-center gap-1 transition-colors cursor-pointer border border-orange-200/80"
              >
                <Icon icon="ph:pencil-simple-bold" className="w-3.5 h-3.5" />
                <span>Ubah</span>
              </button>
            </div>

            {/* Bank Card Item */}
            {bankAccountData?.bank_name ? (
              <div className="p-4 rounded-2xl border border-gray-200 bg-white flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-11 h-11 rounded-xl bg-[#FC480D] text-white font-semibold text-[11px] flex items-center justify-center shrink-0 tracking-wider shadow-2xs">
                    {bankAccountData.bank_name.toUpperCase().slice(0, 4)}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0E1726] truncate">
                      {maskAccountNumber(bankAccountData.account_number)}
                    </div>
                    <div className="text-xs text-gray-400 truncate mt-0.5 font-medium">
                      a/n {bankAccountData.account_holder_name}
                    </div>
                  </div>
                </div>

                <div className="text-emerald-500 shrink-0" title="Rekening Terverifikasi">
                  <Icon icon="ph:check-circle-fill" className="w-5 h-5" />
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50 flex items-center justify-between text-xs text-gray-400">
                <span>Belum ada rekening pencairan terdaftar</span>
                <button
                  type="button"
                  onClick={() => navigate(`${getAdminBasePath()}/profile-settings`)}
                  className="text-[#FC480D] font-semibold hover:underline"
                >
                  Tambah
                </button>
              </div>
            )}
          </div>

          {/* Footer note */}
          <div className="text-[11px] text-gray-400 flex items-center gap-1.5 mt-6 pt-4 border-t border-gray-100">
            <Icon icon="ph:info" className="w-3.5 h-3.5 shrink-0 text-gray-400" />
            <span>Semua pencairan saldo dikirim ke rekening ini.</span>
          </div>
        </div>
      </div>

      {/* Tabs Toolbar: Riwayat Pendapatan vs Riwayat Penarikan (Screenshot 1 & 4) */}
      <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pt-2">
        <button
          type="button"
          onClick={() => setActiveTab('income')}
          className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer shrink-0 ${
            activeTab === 'income'
              ? 'bg-white text-[#FC480D] border border-orange-200/80 shadow-2xs font-semibold'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/60'
          }`}
        >
          <span>Riwayat Pendapatan</span>
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
              activeTab === 'income' ? 'bg-orange-100 text-[#FC480D]' : 'bg-gray-100 text-gray-500'
            }`}
          >
            {totalTransactionCount}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('withdrawals')}
          className={`px-4 py-2 rounded-full text-xs font-semibold flex items-center gap-2 transition-all cursor-pointer shrink-0 ${
            activeTab === 'withdrawals'
              ? 'bg-white text-[#FC480D] border border-orange-200/80 shadow-2xs font-semibold'
              : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100/60'
          }`}
        >
          <span>Riwayat Penarikan</span>
          <span
            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
              activeTab === 'withdrawals' ? 'bg-orange-100 text-[#FC480D]' : 'bg-gray-100 text-gray-500'
            }`}
          >
            {totalWithdrawalCount}
          </span>
        </button>
      </div>

      {/* Table Content: Riwayat Pendapatan (Screenshot 1 & 2) */}
      {activeTab === 'income' && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-2xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#F8FAFC] border-b border-gray-200/80 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  <th
                    className="py-3.5 px-5 cursor-pointer hover:text-gray-800 transition-colors"
                    onClick={() => handleTransactionSort('date')}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>Tanggal</span>
                      <Icon icon="ph:caret-up-down-bold" className="w-3 h-3 text-gray-400" />
                    </div>
                  </th>
                  <th className="py-3.5 px-5">Keterangan</th>
                  <th className="py-3.5 px-5">Jenis Transaksi</th>
                  <th className="py-3.5 px-5 text-center">Tipe</th>
                  <th
                    className="py-3.5 px-5 text-right cursor-pointer hover:text-gray-800 transition-colors"
                    onClick={() => handleTransactionSort('amount')}
                  >
                    <div className="flex items-center justify-end gap-1.5">
                      <span>Jumlah</span>
                      <Icon icon="ph:caret-up-down-bold" className="w-3 h-3 text-gray-400" />
                    </div>
                  </th>
                  <th className="py-3.5 px-5 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading && (!transactionData?.data || transactionData.data.length === 0) ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-xs text-gray-400">
                      <Icon icon="ph:spinner" className="w-6 h-6 text-[#FC480D] animate-spin mx-auto mb-2" />
                      <span>Memuat riwayat transaksi...</span>
                    </td>
                  </tr>
                ) : !transactionData?.data || transactionData.data.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-xs text-gray-400">
                      <Icon icon="ph:receipt-slash-bold" className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="font-semibold text-gray-600">Belum ada riwayat pendapatan</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Transaksi online akan muncul di sini.</p>
                    </td>
                  </tr>
                ) : (
                  transactionData.data.map((item, idx) => (
                    <tr key={item.reference_id || idx} className="hover:bg-gray-50/60 transition-colors">
                      <td className="py-4 px-5 text-xs text-gray-500 font-medium whitespace-nowrap">
                        {formatDateOnly(item.created_at)}
                      </td>
                      <td className="py-4 px-5 text-xs font-semibold text-[#0E1726] max-w-md">
                        {item.description || `Pembayaran Booking: ${item.reference_id || '-'}`}
                      </td>
                      <td className="py-4 px-5 text-xs text-gray-600 font-medium">
                        {item.reference_type === 'booking' ? 'Booking Lapangan' : item.reference_type || 'Booking Lapangan'}
                      </td>
                      <td className="py-4 px-5 text-xs text-gray-500 text-center font-medium">
                        Online
                      </td>
                      <td className="py-4 px-5 text-xs font-semibold text-emerald-600 text-right whitespace-nowrap">
                        Rp. {item.amount.toLocaleString('id-ID')}
                      </td>
                      <td className="py-4 px-5 text-center">
                        {renderTransactionStatusChip(item.status)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Footer (Screenshot 2) */}
          {totalTransactionCount > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-t border-gray-100">
              <div className="text-xs text-gray-400 font-medium">
                Menampilkan {showingStart} - {showingEnd} dari {totalTransactionCount}
              </div>
              <div className="flex items-center gap-1.5 self-center">
                <button
                  type="button"
                  onClick={() => handleTransactionPageChange(Math.max(1, transactionPage - 1))}
                  disabled={transactionPage <= 1}
                  className="w-8 h-8 rounded-full border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-500 disabled:opacity-40 transition-colors cursor-pointer"
                >
                  <Icon icon="ph:caret-left-bold" className="w-3.5 h-3.5" />
                </button>
                {paginationPages.map((p, i) =>
                  p === '...' ? (
                    <span key={`dots-${i}`} className="w-8 h-8 flex items-center justify-center text-xs text-gray-400">
                      ...
                    </span>
                  ) : (
                    <button
                      key={`page-${p}`}
                      type="button"
                      onClick={() => handleTransactionPageChange(p as number)}
                      className={`w-8 h-8 rounded-full text-xs font-semibold flex items-center justify-center transition-colors cursor-pointer ${
                        transactionPage === p
                          ? 'bg-[#FC480D] text-white shadow-2xs font-semibold'
                          : 'border border-gray-200 bg-white hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
                <button
                  type="button"
                  onClick={() => handleTransactionPageChange(Math.min(totalPages, transactionPage + 1))}
                  disabled={transactionPage >= totalPages}
                  className="w-8 h-8 rounded-full border border-gray-200 bg-white hover:bg-gray-50 flex items-center justify-center text-gray-500 disabled:opacity-40 transition-colors cursor-pointer"
                >
                  <Icon icon="ph:caret-right-bold" className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Table Content: Riwayat Penarikan (Screenshot 4) */}
      {activeTab === 'withdrawals' && (
        <div className="bg-white rounded-2xl border border-gray-200/80 shadow-2xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#F8FAFC] border-b border-gray-200/80 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                  <th
                    className="py-3.5 px-5 cursor-pointer hover:text-gray-800 transition-colors"
                    onClick={() => handleWithdrawalSort('date')}
                  >
                    <div className="flex items-center gap-1.5">
                      <span>Tanggal</span>
                      <Icon icon="ph:caret-up-down-bold" className="w-3 h-3 text-gray-400" />
                    </div>
                  </th>
                  <th className="py-3.5 px-5">ID</th>
                  <th className="py-3.5 px-5">Rekening Tujuan</th>
                  <th className="py-3.5 px-5 text-right">Jumlah Tarik</th>
                  <th className="py-3.5 px-5 text-right">Biaya Penarikan</th>
                  <th className="py-3.5 px-5 text-right">Diterima</th>
                  <th className="py-3.5 px-5 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {!withdrawalData?.withdrawals || withdrawalData.withdrawals.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-12 text-center text-xs text-gray-400">
                      <Icon icon="ph:receipt-slash-bold" className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="font-semibold text-gray-600">Belum ada riwayat penarikan dana</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">Penarikan yang diajukan akan tercatat di sini.</p>
                    </td>
                  </tr>
                ) : (
                  withdrawalData.withdrawals.map((item, idx) => {
                    const adminFee = (item as any).admin_fee ?? 2000;
                    const grossAmount = item.total_amount || (item.amount + adminFee);
                    const netAmount = item.amount;

                    return (
                      <tr key={item.withdrawal_code || idx} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-4 px-5 text-xs text-gray-500 font-medium whitespace-nowrap">
                          {formatDateTime(item.requested_at)}
                        </td>
                        <td className="py-4 px-5 text-xs font-semibold text-[#0E1726]">
                          {item.withdrawal_code}
                        </td>
                        <td className="py-4 px-5 text-xs text-gray-600 font-medium">
                          {item.bank_name || 'Bank'} - {maskAccountNumber(item.account_number)}
                        </td>
                        <td className="py-4 px-5 text-xs font-semibold text-[#0E1726] text-right whitespace-nowrap">
                          Rp. {grossAmount.toLocaleString('id-ID')}
                        </td>
                        <td className="py-4 px-5 text-xs text-red-500 font-medium text-right whitespace-nowrap">
                          - Rp. {adminFee.toLocaleString('id-ID')}
                        </td>
                        <td className="py-4 px-5 text-xs font-semibold text-[#0E1726] text-right whitespace-nowrap">
                          Rp. {netAmount.toLocaleString('id-ID')}
                        </td>
                        <td className="py-4 px-5 text-center">
                          {renderWithdrawalStatusChip(item.status)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalWithdrawalCount > 0 && (
            <div className="p-4 border-t border-gray-100 text-xs text-gray-400 font-medium">
              Menampilkan 1 - {totalWithdrawalCount} dari {totalWithdrawalCount}
            </div>
          )}
        </div>
      )}

      {/* Modal: Tarik Saldo (Screenshot 3) */}
      {withdrawModalOpen && (
        <div className="fixed inset-0 bg-[#0E1726]/50 backdrop-blur-xs z-50 flex items-center justify-center p-4 animate-in fade-in duration-150">
          <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xl max-w-md w-full p-6 space-y-4 animate-in zoom-in-95 duration-150">
            <Formik
              initialValues={{
                amount: 0,
              }}
              validationSchema={withdrawValidationSchema}
              onSubmit={(values, { setSubmitting }) => {
                const adminFee = walletData?.withdraw_fee ?? 0;
                const netAmount = Math.max(0, values.amount - adminFee);
                const confirmMessage = `Apakah Anda yakin ingin menarik Rp ${values.amount.toLocaleString('id-ID')} (biaya admin Rp ${adminFee.toLocaleString('id-ID')}, diterima Rp ${netAmount.toLocaleString('id-ID')}) ke rekening ${bankAccountData?.bank_name} - ${bankAccountData?.account_number} A.N ${bankAccountData?.account_holder_name}?`;
                setWithdrawConfirmMessage(confirmMessage);
                setCurrentWithdrawValues(values);
                setWithdrawConfirm(true);
                setSubmitting(false);
              }}
            >
              {({ values, setFieldValue, isSubmitting }) => {
                const adminFee = walletData?.withdraw_fee ?? 0;
                const balance = walletData?.balance || 0;
                const maxWithdraw = balance;
                const minWithdraw = 13000 + adminFee;
                const totalReceived = Math.max(0, values.amount - adminFee);
                const canWithdraw =
                  maxWithdraw >= minWithdraw && bankAccountData?.bank_name !== undefined;

                return (
                  <Form className="space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                      <h3 className="text-base font-semibold text-[#0E1726]">Tarik Saldo</h3>
                      <button
                        type="button"
                        onClick={() => setWithdrawModalOpen(false)}
                        className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
                      >
                        <Icon icon="ph:x-bold" className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Siap Ditarik Box */}
                    <div className="p-3.5 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-between text-xs">
                      <span className="text-gray-500 font-medium">Siap ditarik</span>
                      <span className="font-semibold text-[#0E1726]">
                        Rp. {balance.toLocaleString('id-ID')}
                      </span>
                    </div>

                    {/* Input Jumlah Penarikan */}
                    <div>
                      <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                        Jumlah penarikan
                      </label>
                      <div className="relative">
                        <span className="absolute left-3.5 top-3 text-xs font-semibold text-gray-400">
                          Rp.
                        </span>
                        <Field
                          name="amount"
                          type="number"
                          placeholder="0"
                          max={maxWithdraw}
                          disabled={!canWithdraw}
                          value={values.amount || ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const raw = e.target.value;
                            if (raw === '') {
                              setFieldValue('amount', 0);
                              return;
                            }
                            const next = Number(raw);
                            setFieldValue(
                              'amount',
                              Number.isFinite(next) ? Math.min(Math.max(next, 0), maxWithdraw) : 0
                            );
                          }}
                          className="w-full h-11 pl-11 pr-4 bg-white border border-gray-200 rounded-xl text-xs font-semibold text-[#0E1726] placeholder:text-gray-400 focus:outline-none focus:border-[#FC480D] focus:ring-1 focus:ring-[#FC480D] transition-colors"
                        />
                      </div>
                      <ErrorMessage
                        name="amount"
                        component="div"
                        className="text-red-500 text-[11px] mt-1"
                      />

                      {/* Quick Shortcut Pills */}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          type="button"
                          onClick={() => setFieldValue('amount', Math.min(100000, maxWithdraw))}
                          disabled={!canWithdraw}
                          className="h-7 px-3 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-[11px] font-semibold text-gray-600 transition-colors cursor-pointer"
                        >
                          Rp. 100.000
                        </button>
                        <button
                          type="button"
                          onClick={() => setFieldValue('amount', Math.min(500000, maxWithdraw))}
                          disabled={!canWithdraw}
                          className="h-7 px-3 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-[11px] font-semibold text-gray-600 transition-colors cursor-pointer"
                        >
                          Rp. 500.000
                        </button>
                        <button
                          type="button"
                          onClick={() => setFieldValue('amount', maxWithdraw)}
                          disabled={!canWithdraw}
                          className="h-7 px-3 rounded-full border border-gray-200 bg-white hover:bg-gray-50 text-[11px] font-semibold text-gray-600 transition-colors cursor-pointer"
                        >
                          Tarik semua
                        </button>
                      </div>
                    </div>

                    {/* Rekening Tujuan Box */}
                    <div>
                      <label className="text-xs font-semibold text-[#0E1726] mb-1.5 block">
                        Rekening tujuan
                      </label>
                      <div className="p-3.5 rounded-xl border border-gray-200 bg-white flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-[#FC480D] text-white font-semibold text-[10px] flex items-center justify-center shrink-0 tracking-wider">
                          {bankAccountData?.bank_name?.toUpperCase().slice(0, 4) || 'BANK'}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-[#0E1726] truncate">
                            {maskAccountNumber(bankAccountData?.account_number)}
                          </div>
                          <div className="text-[11px] text-gray-400 truncate mt-0.5 font-medium">
                            a/n {bankAccountData?.account_holder_name || '-'}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Rincian Penarikan Box */}
                    <div className="p-4 rounded-xl bg-orange-50/40 border border-orange-100 space-y-2 text-xs">
                      <div className="flex items-center justify-between text-gray-600">
                        <span>Jumlah tarik</span>
                        <span className="font-medium text-[#0E1726]">
                          Rp. {values.amount.toLocaleString('id-ID')}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-gray-600">
                        <span>Biaya penarikan</span>
                        <span className="font-medium text-red-500">
                          - Rp. {adminFee.toLocaleString('id-ID')}
                        </span>
                      </div>
                      <div className="border-t border-orange-200/50 my-1" />
                      <div className="flex items-center justify-between text-xs font-semibold">
                        <span className="text-[#0E1726]">Diterima di rekening</span>
                        <span className="text-[#FC480D]">
                          Rp. {totalReceived.toLocaleString('id-ID')}
                        </span>
                      </div>
                    </div>

                    {/* Modal Footer Buttons */}
                    <div className="flex items-center justify-end gap-2.5 pt-2">
                      <button
                        type="button"
                        onClick={() => setWithdrawModalOpen(false)}
                        className="h-10 px-6 rounded-full bg-white hover:bg-gray-100 text-gray-700 text-xs font-semibold transition-all cursor-pointer"
                      >
                        Batal
                      </button>
                      <button
                        type="submit"
                        disabled={
                          values.amount < minWithdraw ||
                          values.amount > maxWithdraw ||
                          !canWithdraw ||
                          isSubmitting
                        }
                        className="h-10 px-6 rounded-full bg-[#FC480D] hover:bg-[#E8490F] disabled:opacity-40 text-white text-xs font-semibold transition-all cursor-pointer shadow-2xs active:scale-[0.98]"
                      >
                        Tarik Saldo
                      </button>
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </div>
      )}

      {/* Notification Toast */}
      <Notification
        title="Penarikan Berhasil"
        message={
          !withdrawalIsError
            ? `Permintaan penarikan dana sebesar Rp ${withdrawAmount.toLocaleString('id-ID')} telah berhasil diproses dengan kode penarikan ${withdrawalCode}. Silakan tunggu proses transfer.`
            : `Terjadi kesalahan saat melakukan penarikan dana, silakan coba lagi nanti atau hubungi CS.`
        }
        variant={withdrawalIsError ? 'error' : 'success'}
        isOpen={notificationOpen}
        onClose={() => setNotificationOpen(false)}
      />

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={withdrawConfirm}
        title="Konfirmasi Penarikan Dana"
        message={withdrawConfirmMessage}
        onConfirm={() => setIsWithdrawConfirmed(true)}
        onCancel={() => {
          setIsWithdrawConfirmed(false);
          setWithdrawConfirmMessage('');
          setWithdrawConfirm(false);
        }}
        loading={isSubmitWithdraw}
        confirmText="Ya, Tarik dana sekarang"
        cancelText="Batal"
      />
    </div>
  );
};

export default Wallet;
